import React, { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import DashboardShell from "@/components/DashboardShell";
import LockedGate from "@/components/LockedGate";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Flame, Hash, UserCircle, TrendingUp, Loader2, Copy, Rocket, Bookmark } from "lucide-react";

const ALL_PLATFORMS = [
  { id: "tiktok", name: "TikTok" }, { id: "instagram", name: "Instagram" },
  { id: "youtube", name: "YouTube" }, { id: "facebook", name: "Facebook" },
];
const copy = (t) => { navigator.clipboard.writeText(t).then(() => toast.success("Copied!")).catch(() => {}); };

const PlatformSelect = ({ value, onChange, platforms, testid }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className="h-11 bg-transparent border-white/10 w-full sm:w-44" data-testid={testid}><SelectValue /></SelectTrigger>
    <SelectContent>{platforms.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
  </Select>
);
const Panel = ({ children }) => <div className="rounded-xl border border-border/50 bg-card p-6 space-y-4">{children}</div>;
const Tag = ({ children }) => <Badge className="bg-primary/10 text-primary border border-primary/20">#{String(children).replace(/^#/, "")}</Badge>;

const GrowSuite = () => {
  const { user } = useAuth();
  const platforms = (user?.platforms?.length ? user.platforms.map((p) => ({ id: p.platform, name: p.platform })) : ALL_PLATFORMS)
    .map((p) => ({ id: p.id, name: ALL_PLATFORMS.find((x) => x.id === p.id)?.name || p.id }));
  const defaultPf = platforms[0]?.id || "tiktok";
  const locked = user && user.role !== "admin" && user.subscription_status !== "active";

  const [pf, setPf] = useState(defaultPf);
  const [topic, setTopic] = useState("");
  const [htTopic, setHtTopic] = useState("");
  const [bio, setBio] = useState("");
  const [handle, setHandle] = useState("");
  const [trendPf, setTrendPf] = useState(defaultPf);
  const [busy, setBusy] = useState("");
  const [res, setRes] = useState({});

  const run = async (key, fn) => {
    setBusy(key);
    try { const data = await fn(); setRes((r) => ({ ...r, [key]: data })); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Generation failed"); }
    finally { setBusy(""); }
  };

  const saveLib = async (payload) => {
    try { await api.post("/library", payload); toast.success("Saved to your Library"); }
    catch { toast.error("Could not save to Library"); }
  };

  if (locked) {
    return <LockedGate testid="grow-locked" title="Unlock the Viral Lab"
      blurb="AI-powered viral idea generator, hashtag & keyword optimizer, profile optimizer, and a live trending feed — all tuned to your niche." />;
  }

  return (
    <DashboardShell>
      <div className="p-6 md:p-10 max-w-4xl mx-auto">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-primary/15 text-primary flex items-center justify-center"><Rocket className="w-5 h-5" /></div>
          <h1 className="font-heading text-3xl font-black tracking-tight">Viral Lab</h1>
        </div>
        <p className="text-muted-foreground mb-8">Spin up viral ideas, dominate discovery, sharpen your profile and ride what's trending right now.</p>

        <Tabs defaultValue="ideas">
          <TabsList className="bg-card border border-border/50 flex-wrap h-auto">
            <TabsTrigger value="ideas" data-testid="grow-tab-ideas"><Flame className="w-4 h-4 mr-1" /> Viral Ideas</TabsTrigger>
            <TabsTrigger value="hashtags" data-testid="grow-tab-hashtags"><Hash className="w-4 h-4 mr-1" /> Hashtags</TabsTrigger>
            <TabsTrigger value="profile" data-testid="grow-tab-profile"><UserCircle className="w-4 h-4 mr-1" /> Profile</TabsTrigger>
            <TabsTrigger value="trending" data-testid="grow-tab-trending"><TrendingUp className="w-4 h-4 mr-1" /> Trending</TabsTrigger>
          </TabsList>

          {/* VIRAL IDEAS */}
          <TabsContent value="ideas" className="mt-6">
            <Panel>
              <div className="flex flex-col sm:flex-row gap-3">
                <PlatformSelect value={pf} onChange={setPf} platforms={platforms} testid="grow-ideas-platform" />
                <Input value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="Optional: a topic or theme (leave blank to use your niche)"
                  className="h-11 bg-transparent border-white/10 flex-1" data-testid="grow-ideas-input" />
                <Button onClick={() => run("ideas", async () => (await api.post("/grow/viral-ideas", { platform: pf, topic })).data.result)}
                  disabled={busy === "ideas"} className="h-11 rounded-full bg-primary text-background font-bold px-6" data-testid="grow-ideas-btn">
                  {busy === "ideas" ? <Loader2 className="w-4 h-4 animate-spin" /> : "Generate ideas"}
                </Button>
              </div>
              {res.ideas && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="grid sm:grid-cols-2 gap-3 pt-2" data-testid="grow-ideas-result">
                  {res.ideas.ideas?.map((it, i) => (
                    <div key={i} className="rounded-lg border border-white/10 p-4 space-y-2">
                      <div className="flex items-start justify-between gap-2">
                        <div className="font-heading font-semibold flex items-center gap-2"><Flame className="w-4 h-4 text-secondary" />{it.title}</div>
                        <button onClick={() => saveLib({ type: "idea", platform: pf, title: it.title, content: it })} title="Save to Library" className="text-muted-foreground hover:text-primary shrink-0" data-testid={`grow-idea-save-${i}`}><Bookmark className="w-4 h-4" /></button>
                      </div>
                      <div className="text-sm"><span className="text-primary font-medium">Hook:</span> {it.hook}</div>
                      <div className="text-xs text-muted-foreground">🎬 {it.format}</div>
                      <div className="text-xs text-primary">🚀 {it.why_viral}</div>
                      <div className="flex flex-wrap gap-1.5">{it.hashtags?.map((h, j) => <Tag key={j}>{h}</Tag>)}</div>
                    </div>
                  ))}
                </motion.div>
              )}
            </Panel>
          </TabsContent>

          {/* HASHTAGS */}
          <TabsContent value="hashtags" className="mt-6">
            <Panel>
              <div className="flex flex-col sm:flex-row gap-3">
                <PlatformSelect value={pf} onChange={setPf} platforms={platforms} testid="grow-hashtags-platform" />
                <Input value={htTopic} onChange={(e) => setHtTopic(e.target.value)} placeholder="What's the post about?"
                  className="h-11 bg-transparent border-white/10 flex-1" data-testid="grow-hashtags-input" />
                <Button onClick={() => htTopic.trim() ? run("hashtags", async () => (await api.post("/grow/hashtags", { platform: pf, topic: htTopic })).data.result) : toast.error("Enter a topic")}
                  disabled={busy === "hashtags"} className="h-11 rounded-full bg-primary text-background font-bold px-6" data-testid="grow-hashtags-btn">
                  {busy === "hashtags" ? <Loader2 className="w-4 h-4 animate-spin" /> : "Optimize"}
                </Button>
              </div>
              {res.hashtags && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4 pt-2" data-testid="grow-hashtags-result">
                  {[["Broad reach", res.hashtags.broad], ["Niche", res.hashtags.niche], ["Trending", res.hashtags.trending]].map(([label, arr]) => (
                    <div key={label}>
                      <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2 flex items-center justify-between">
                        {label}
                        <button onClick={() => copy((arr || []).map((h) => "#" + h.replace(/^#/, "")).join(" "))} className="text-primary hover:underline" data-testid={`grow-copy-${label.split(" ")[0].toLowerCase()}`}><Copy className="w-3.5 h-3.5 inline" /> copy</button>
                      </div>
                      <div className="flex flex-wrap gap-1.5">{arr?.map((h, i) => <Tag key={i}>{h}</Tag>)}</div>
                    </div>
                  ))}
                  <div>
                    <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">SEO keywords</div>
                    <div className="flex flex-wrap gap-1.5">{res.hashtags.keywords?.map((k, i) => <Badge key={i} variant="outline" className="border-white/15">{k}</Badge>)}</div>
                  </div>
                  <ul className="space-y-1.5 pt-1">{res.hashtags.tips?.map((t, i) => <li key={i} className="text-sm text-muted-foreground flex gap-2">✓ {t}</li>)}</ul>
                  <Button variant="outline" size="sm" onClick={() => saveLib({ type: "hashtags", platform: pf, title: `Hashtags: ${htTopic}`, content: { topic: htTopic, ...res.hashtags } })} className="rounded-full border-white/15 mt-1" data-testid="grow-hashtags-save"><Bookmark className="w-3.5 h-3.5 mr-1" /> Save set to Library</Button>
                </motion.div>
              )}
            </Panel>
          </TabsContent>

          {/* PROFILE */}
          <TabsContent value="profile" className="mt-6">
            <Panel>
              <div className="flex flex-col sm:flex-row gap-3">
                <PlatformSelect value={pf} onChange={setPf} platforms={platforms} testid="grow-profile-platform" />
                <Input value={handle} onChange={(e) => setHandle(e.target.value)} placeholder="@handle (optional)"
                  className="h-11 bg-transparent border-white/10 sm:w-48" data-testid="grow-profile-handle" />
              </div>
              <Textarea value={bio} onChange={(e) => setBio(e.target.value)} placeholder="Paste your current bio here" className="bg-transparent border-white/10 min-h-[90px]" data-testid="grow-profile-bio" />
              <Button onClick={() => bio.trim() ? run("profile", async () => (await api.post("/grow/profile-audit", { platform: pf, handle, bio })).data.result) : toast.error("Paste your current bio")}
                disabled={busy === "profile"} className="h-11 rounded-full bg-primary text-background font-bold px-6" data-testid="grow-profile-btn">
                {busy === "profile" ? <Loader2 className="w-4 h-4 animate-spin" /> : "Audit my profile"}
              </Button>
              {res.profile && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4 pt-2" data-testid="grow-profile-result">
                  <div className="flex items-center gap-4 rounded-lg bg-primary/5 border border-primary/20 p-4">
                    <div className="text-4xl font-black text-primary" data-testid="grow-profile-score">{res.profile.score}<span className="text-lg text-muted-foreground">/100</span></div>
                    <div className="text-sm">{res.profile.verdict}</div>
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Name ideas</div>
                    <div className="flex flex-wrap gap-1.5">{res.profile.name_ideas?.map((n, i) => <Badge key={i} variant="outline" className="border-white/15">{n}</Badge>)}</div>
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Rewritten bios</div>
                    <div className="space-y-2">{res.profile.bio_options?.map((b, i) => (
                      <div key={i} className="rounded-lg border border-white/10 p-3 flex items-start justify-between gap-3"><div className="text-sm font-medium">{b}</div><Button variant="ghost" size="sm" onClick={() => copy(b)} className="shrink-0"><Copy className="w-3.5 h-3.5" /></Button></div>
                    ))}</div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div><div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Improvements</div><ul className="space-y-1.5">{res.profile.improvements?.map((t, i) => <li key={i} className="text-sm text-muted-foreground flex gap-2">→ {t}</li>)}</ul></div>
                    <div><div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">CTA ideas</div><ul className="space-y-1.5">{res.profile.cta_ideas?.map((t, i) => <li key={i} className="text-sm text-muted-foreground flex gap-2">💬 {t}</li>)}</ul></div>
                  </div>
                </motion.div>
              )}
            </Panel>
          </TabsContent>

          {/* TRENDING */}
          <TabsContent value="trending" className="mt-6">
            <Panel>
              <div className="flex gap-3">
                <PlatformSelect value={trendPf} onChange={setTrendPf} platforms={platforms} testid="grow-trending-platform" />
                <Button onClick={() => run("trending", async () => (await api.get(`/grow/trending?platform=${trendPf}`)).data)}
                  disabled={busy === "trending"} className="h-11 rounded-full bg-primary text-background font-bold px-6" data-testid="grow-trending-btn">
                  {busy === "trending" ? <Loader2 className="w-4 h-4 animate-spin" /> : "What's trending"}
                </Button>
              </div>
              {res.trending && res.trending.kind === "youtube" && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="grid sm:grid-cols-2 gap-3 pt-2" data-testid="grow-trending-result">
                  {res.trending.items?.map((v, i) => (
                    <a key={i} href={v.url} target="_blank" rel="noreferrer" className="rounded-lg border border-white/10 p-3 flex gap-3 hover:border-primary/40 transition-colors">
                      {v.thumbnail && <img src={v.thumbnail} alt="" className="w-24 h-16 object-cover rounded-md shrink-0" />}
                      <div className="min-w-0">
                        <div className="text-sm font-semibold line-clamp-2">{v.title}</div>
                        <div className="text-xs text-muted-foreground mt-1">{v.channel}</div>
                        <div className="text-xs text-primary mt-1">{Intl.NumberFormat("en", { notation: "compact" }).format(v.views)} views</div>
                      </div>
                    </a>
                  ))}
                </motion.div>
              )}
              {res.trending && res.trending.kind === "ai" && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="grid sm:grid-cols-2 gap-3 pt-2" data-testid="grow-trending-result">
                  {res.trending.items?.map((t, i) => (
                    <div key={i} className="rounded-lg border border-white/10 p-4">
                      <div className="flex items-center justify-between">
                        <div className="font-heading font-semibold flex items-center gap-2"><TrendingUp className="w-4 h-4 text-primary" />{t.title}</div>
                        <Badge className={`text-[10px] ${t.heat === "hot" ? "bg-secondary/15 text-secondary border-secondary/30" : "bg-primary/10 text-primary border-primary/20"} border`}>{t.heat}</Badge>
                      </div>
                      <div className="text-xs uppercase tracking-widest text-muted-foreground mt-2">{t.type}</div>
                      <div className="text-sm mt-1">{t.angle}</div>
                      <div className="text-xs text-primary mt-2">Why now: {t.why_now}</div>
                    </div>
                  ))}
                </motion.div>
              )}
            </Panel>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardShell>
  );
};

export default GrowSuite;
